import { Paper, Table, TableBody, TableCell, TableContainer, TableHead } from '@mui/material';
import React, { useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { getLocalParticipant } from '../../../base/participants';
import SpeakerStatsItemV3 from './SpeakerStatsItemV3';
import { SPEAKER_STATS_RELOAD_INTERVAL } from '../../constants';
import { initUpdateStats } from '../../actions.any';
import { makeStyles } from 'tss-react/mui';
const TABLE_HEAD_CELL = ['#', 'status', 'name', 'time', 'delay', 'connect'];
const useStyles = makeStyles()(theme => {
    return {
        tableContainer: {
            color: theme.palette.default00,
            overflowX: 'initial',
            overflowY: 'auto',
            boxShadow: 'none',
            marginTop: 12,
            borderRadius: 0,
            maxHeight: 288
        },
        tableCell: {
            padding: '11px 12px 12px 12px',
            whiteSpace: 'nowrap'
        },
        noData: {
            display: 'block',
            textAlign: 'center',
            padding: '12px 0'
        },
        total: {
            display: 'block',
            marginTop: 12,
            fontSize: 15,
            fontWeight: 500
        }
    };
});
const SpeakerStatsTable = () => {
    const { t } = useTranslation();
    const { cx, classes } = useStyles();
    const { stats: speakerStats, sortedSpeakerStatsIds, filterActive, filterDisplayName } = useSelector((state) => state['features/speaker-stats']);
    const { conference } = useSelector((state) => state['features/base/conference']);
    const localParticipant = useSelector(getLocalParticipant);
    const { faceLandmarks: faceLandmarksConfig } = useSelector((state) => state['features/base/config']) || {};
    const { faceLandmarks } = useSelector((state) => state['features/face-landmarks'])
        || { faceLandmarks: [] };
    const reloadInterval = useRef();
    const dispatch = useDispatch();
    const getSpeakerStats = useCallback(() => {
        const stats = conference?.getSpeakerStats();
        for (const userId in stats) {
            if (stats[userId]) {
                if (stats[userId].isLocalStats()) {
                    const meString = t('me');
                    stats[userId].setDisplayName(localParticipant?.name
                        ? `${localParticipant.name} (${meString})`
                        : meString);
                    if (faceLandmarksConfig?.enableDisplayFaceExpressions) {
                        stats[userId].setFaceLandmarks(faceLandmarks);
                    }
                }
                if (!stats[userId].getDisplayName()) {
                    stats[userId].setDisplayName(conference?.getParticipantById(userId)?.name);
                }
            }
        }
        return stats ?? {};
    }, [faceLandmarks]);
    const localSpeakerStats = Object.keys(speakerStats).length === 0 ? getSpeakerStats() : speakerStats;
    const localSortedSpeakerStatsIds = sortedSpeakerStatsIds.length === 0 ? Object.keys(localSpeakerStats) : sortedSpeakerStatsIds;
    const userIds = localSortedSpeakerStatsIds.filter(id => localSpeakerStats[id] && !localSpeakerStats[id].hidden);
    const _userFilter = userIds.map((participantId) => {
        const statsModel = localSpeakerStats[participantId];
        const _filterDisplayName = filterDisplayName.trim().toLowerCase();
        const displayName = statsModel.getDisplayName();
        const isDominantSpeaker = statsModel.isDominantSpeaker();
        const dominantSpeakerTime = statsModel.getTotalDominantSpeakerTime();
        if ((filterActive === undefined || Boolean(filterActive) === isDominantSpeaker) &&
            (_filterDisplayName === '' || displayName.toLowerCase().includes(_filterDisplayName)))
            return {
                participantId,
                displayName,
                isDominantSpeaker,
                dominantSpeakerTime
            };
        return undefined;
    }).filter(user => user !== undefined);
    const updateStats = useCallback(() => dispatch(initUpdateStats(getSpeakerStats)), [dispatch, initUpdateStats, getSpeakerStats]);
    useEffect(() => {
        reloadInterval.current = window.setInterval(() => {
            updateStats();
        }, SPEAKER_STATS_RELOAD_INTERVAL);
        return () => {
            if (reloadInterval.current) {
                clearInterval(reloadInterval.current);
            }
        };
    }, [faceLandmarks]);
    return (React.createElement(TableContainer, { component: Paper, className: classes.tableContainer },
        React.createElement(Table, { stickyHeader: true, "aria-labelledby": "tableTitle", size: 'small' },
            React.createElement(TableHead, null,
                TABLE_HEAD_CELL.map((cell, index) => React.createElement(TableCell, { className: cx(classes.tableCell), key: index }, t(`stats.tableHead.${cell}`))),
                React.createElement(TableCell, { className: cx(classes.tableCell), sx: { maxWidth: 88, textAlign: 'center' } }, t(`stats.tableHead.detail`))),
            React.createElement(TableBody, null, Boolean(_userFilter.length) && _userFilter.map((user, index) => user && React.createElement(SpeakerStatsItemV3, { key: index, index: index, displayName: user.displayName, isDominantSpeaker: user.isDominantSpeaker, participantId: user.participantId, dominantSpeakerTime: user.dominantSpeakerTime })))),
        !Boolean(_userFilter.length) && React.createElement("span", { className: classes.noData }, t('stats.notFound')),
        Boolean(_userFilter.length) && React.createElement("span", { className: classes.total }, t('stats.total', { num: _userFilter.length }))));
};
export default SpeakerStatsTable;
